import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Spin,
  Form,
  Card,
  InputNumber,
  Input,
  Dropdown,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import Settings01 from "@untitled-ui/icons-react/build/esm/Settings01";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import {
  staticConfig,
  apiConfig,
} from "../treasuryContractBooking.config";
import { BOM } from "../../../services/definedMethod";

const STATUS_COLORS = {
  Pending:    { bg: "#FFFAEB", color: "#B54708" },
  Confirmed:  { bg: "#ECFDF3", color: "#027A48" },
  Instructed: { bg: "#EFF8FF", color: "#175CD3" },
  Failed:     { bg: "#FEF3F2", color: "#B42318" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || { bg: "#F2F4F7", color: "#344054" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: 99,
        fontSize: 13,
        fontWeight: 600,
        background: s.bg,
        color: s.color,
      }}
    >
      {status}
    </span>
  );
};

const Tip = ({ text, tip }) => (
  <span>
    {text}
    <Tooltip title={tip}>
      <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
    </Tooltip>
  </span>
);

const ViewSettlement = (props) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [statusDescription, setStatusDescription] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const settlementId = location.state;
  const { userData } = props;
  const { session } = userData || {};

  useEffect(() => {
    if (settlementId) fetchSettlementDetail(settlementId);
  }, [settlementId]);

  const fetchSettlementDetail = (id) => {
    setIsLoading(true);

    const body = { session };
    const url = `${apiConfig.GetSettlements}/settlements/${id}`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Failed to load settlement details.",
        );
        setIsLoading(false);
        return;
      }

      setStatusDescription(res.statusDescription);

      form.setFieldsValue({
        settlementId:              res.settlementId,
        tradeId:                   res.tradeId,
        cashAmount:                res.cashAmount          ?? null,
        cashCurrencyCode:          res.cashCurrencyCode    ?? "—",
        settlementMethodDescription: res.settlementMethodDescription ?? "—",
        statusDescription:         res.statusDescription   ?? "—",
      });

      setIsLoading(false);
    });
  };

  const runApiAction = (urlSuffix, successMsg) => {
    setIsLoading(true);
    const body = { session };
    const url = `${apiConfig.GetSettlements}/settlements/${settlementId}/${urlSuffix}`;

    BOM.FetchReqAction(body, url, (err) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.errors?.[0]?.defaultUserMessage ||
            err?.defaultUserMessage ||
            "Action failed. Please try again.",
        );
        setIsLoading(false);
        return;
      }
      BOM.AlertMsg(cnt.SUCCESS, successMsg);
      setIsLoading(false);
      fetchSettlementDetail(settlementId);
    });
  };

  const handleAction = ({ key }) => {
    switch (key) {
      case "instruct":
        navigate("/settlement/instruct-settlement", { state: settlementId });
        break;
      case "confirm":
        navigate("/settlement/confirm", { state: settlementId });
        break;
      case "fail":
        navigate("/settlement/fail", { state: settlementId });
        break;
      default:
        break;
    }
  };

  const actionMenuItems = [
    { key: "instruct", label: "Instruct Settlement" },
    { key: "confirm",  label: "Confirm Settlement" },
    { type: "divider" },
    {
      key: "fail",
      label: <span style={{ color: "#B42318" }}>Fail Settlement</span>,
    },
  ];

  const ds = { height: 44, background: "#F9FAFB", cursor: "not-allowed" };

  return (
    <MainLayout pageItems={staticConfig.manageSettlement}>
      <Spin spinning={isLoading} size="large">
        <div style={{ padding: "24px" }}>
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
            }}
          >
            {/* Header */}
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 24 }}
            >
              <Col style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <h3 style={{ margin: 0, fontWeight: 600 }}>
                  Settlement Details
                </h3>
                
              </Col>
              <Col>
                <Dropdown
                  menu={{ items: actionMenuItems, onClick: handleAction }}
                  trigger={["click"]}
                  placement="bottomRight"
                >
                  <button
                    type="button"
                    style={{
                      height: 44,
                      padding: "0 20px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <Settings01 width={16} height={16} color="#344054" />
                    Actions
                    <ChevronDown width={16} height={16} color="#667085" />
                  </button>
                </Dropdown>
              </Col>
            </Row>

            <Form layout="vertical" form={form}>

              {/* Settlement ID & Trade ID */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Settlement ID"
                        tip="The unique identifier for this settlement."
                      />
                    }
                    name="settlementId"
                  >
                    <InputNumber style={{ width: "100%", ...ds }} disabled />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Trade ID"
                        tip="The trade this settlement is linked to."
                      />
                    }
                    name="tradeId"
                  >
                    <InputNumber style={{ width: "100%", ...ds }} disabled />
                  </Form.Item>
                </Col>
              </Row>

              {/* Cash Amount & Cash Currency */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Cash Amount"
                        tip="The cash leg of the settlement — the actual amount to be paid or received."
                      />
                    }
                    name="cashAmount"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Cash Currency"
                        tip="The currency in which the cash amount is denominated."
                      />
                    }
                    name="cashCurrencyCode"
                  >
                    <Input style={{ ...ds }} disabled />
                  </Form.Item>
                </Col>
              </Row>

              {/* Settlement Method & Status */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Settlement Method"
                        tip="The method used to settle this transaction."
                      />
                    }
                    name="settlementMethodDescription"
                  >
                    <Input style={{ ...ds }} disabled />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Status"
                        tip="The current status of this settlement."
                      />
                    }
                    name="statusDescription"
                  >
                    <Input style={{ ...ds }} disabled />
                  </Form.Item>
                </Col>
              </Row>

              <div
                style={{ borderTop: "1px solid #EAECF0", margin: "8px 0 20px" }}
              />

              <Row justify="end">
                <Col>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    Back
                  </button>
                </Col>
              </Row>
            </Form>
          </Card>
        </div>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  rightBtn: state.RightSideBtn,
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps, { rightAction })(ViewSettlement);